import {
  Box,
  ButtonGroup,
  IconButton,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@material-ui/core";
import keyBy from "lodash/keyBy";
import * as React from "react";
import { useState } from "react";
import { Datagrid, FunctionField, ListContextProvider, Pagination, useNotify } from "react-admin";
import { useAppDataProvider } from "../../dataprovider";

import RemoveCircleOutline from "@material-ui/icons/RemoveCircleOutline";
import { Skeleton } from "@material-ui/lab";
import { EnumField } from "../../../src/Components/EnumField";
import { CustomDateField } from "../../Components/CustomDateField";
import CreatedByField from "../../../src/Components/ReferenceFields/CreatedBy/CreatedByField";
import { DownloadButton } from "@airborne/react-admin";

const CoreInfoRequestFileGrid = props => {
  const [fileList, setFileList] = useState([] as any);
  const [page, setPage] = useState(1);
  const perPage = 5;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any>(null);
  const [validationError, setValidationError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const notify = useNotify();
  const provider = useAppDataProvider();

  React.useEffect(() => {
    let isMounted = true;

    const fetchFileList = async () => {
      try {
        const { data } = await provider.getList(props.source, {
          pagination: { page: 1, perPage: 99999 },
          sort: { field: "Id", order: "DESC" },
          filter: props.filter,
        });
        if (isMounted) {
          setFileList(data);
          if (props.onFilesChange) props.onFilesChange(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching file list:", error);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchFileList();
    if (props?.triggeted !== null && props?.triggeted !== undefined && props?.trigged) props?.triggeted();

    return () => {
      isMounted = false;
    };
  }, [props?.trigged, JSON.stringify(props.filter)]);

  const getFileList = async () => {
    const { data } = await provider.getList(props.source, {
      pagination: { page: 1, perPage: 99999 },
      sort: { field: "Id", order: "DESC" },
      filter: props.filter,
    });
    setFileList(data);
    if (props.onFilesChange) props.onFilesChange(data);
  };

  const GetPaginationData = () => {
    return fileList.slice((page - 1) * perPage, perPage * page);
  };

  const validateDeletion = recordToDelete => {
    if (!recordToDelete) return { canDelete: true, message: "" };

    // Check if this is the last document
    if (fileList.length <= 1) {
      return {
        canDelete: false,
        message: "You cannot delete this document without uploading the second document of the required file type.",
      };
    }

    return { canDelete: true, message: "" };
  };

  const removeButtonClick = record => {
    setRecordToDelete(record);
    const validation = validateDeletion(record);
    setValidationError(validation.canDelete ? "" : validation.message);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (recordToDelete && !validationError) {
      await provider
        .deleteFileRelation(recordToDelete.id, props.source)
        .then(result => {
          notify(`Record Deleted`, "success", false, true);
          getFileList();
          props.triggeted();
        })
        .catch(error => {
          notify(`Error : ${error}`, "error", false, true);
        })
        .finally(() => {
          setConfirmDialogOpen(false);
          setRecordToDelete(null);
          setValidationError("");
        });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setRecordToDelete(null);
    setValidationError("");
  };

  return (
    <Paper elevation={4}>
      <div>
        {isLoading ? (
          <Box p={2}>
            <Skeleton variant="rect" height={200} style={{ marginBottom: 8, borderRadius: 4 }} />
          </Box>
        ) : (
          <ListContextProvider
            value={{
              data: keyBy(GetPaginationData(), "id"),
              ids: GetPaginationData().map(({ id }) => id),
              currentSort: { field: "id", order: "DESC" },
            }}
          >
            <Datagrid {...props} optimized={true}>
              {props.canEdit && (
                <FunctionField
                  render={(record: any) => (
                    <Box style={{ flex: 1, flexDirection: "row" }}>
                      <ButtonGroup variant="text" size="small">
                        {record.coreInfoRequestFileType && (
                          <IconButton
                            onClick={e => {
                              e.stopPropagation();
                              removeButtonClick(record);
                            }}
                            color="primary"
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        )}
                      </ButtonGroup>
                    </Box>
                  )}
                />
              )}
              <FunctionField
                style={{ fontSize: 12 }}
                label="Name"
                render={(record: any) => (
                  <DownloadButton target={`download/${record.objectKey}`} buttonText={record.name} variant="text" />
                )}
              />
              <EnumField
                label={"Core Info Request Type"}
                enumName={"CoreInfoRequestReasonTypeEnum"}
                source={"coreInfoRequestFileType"}
              />
              <EnumField
                label={"Core Unit File Type"}
                enumName={"PbhRequestFileType"}
                source={"unserviceableFileType"}
              />
              <CustomDateField source="createdDate" label="CREATED DATE" />
              <CreatedByField label="CREATED BY" link={false} />
            </Datagrid>
            <Pagination
              //@ts-ignore
              page={page}
              perPage={perPage}
              setPage={setPage}
              total={fileList.length}
            />
          </ListContextProvider>
        )}
      </div>
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {validationError ? "Cannot Delete Document" : "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {validationError ? validationError : `Are you sure you want to delete document "${recordToDelete?.name}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {validationError ? "OK" : "Cancel"}
          </Button>
          {!validationError && (
            <Button onClick={handleConfirmDelete} color="primary" autoFocus>
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CoreInfoRequestFileGrid;
